import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams } from '../../utils/utilities';
import { setModalContent, setShowModal } from '../utils/utils.slice';
import { DataPlanType } from '../../types/data-plan/data-plan.type';
import { DataPlanTypeType } from '../../types/data-plan/data-plan-type/data-plan-type.type';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';

export const postDataPlanDetails = createAsyncThunk<DataPlanType, { data: unknown }, { state: RootReducerState }>(
  'dataPlans/postDataPlanDetails',
  async ({ data }, thunkAPI) => {
    try {
      const response = await axios.post<DataPlanType>(`${process.env.REACT_APP_API_URL}/data-plans`, data);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating data plan!' });
    }
  }
);

export const getDataPlansList = createAsyncThunk<
  PaginationType<DataPlanType>,
  RouteParamsType & {
    controller?: AbortController;
  },
  { state: RootReducerState }
>('dataPlans/getDataPlansList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<DataPlanType>>(
      `${process.env.REACT_APP_API_URL}/data-plans${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching data plans!' });
  }
});

export const getDataPlanDetails = createAsyncThunk<DataPlanType, { id: number }, { state: RootReducerState }>(
  'dataPlans/getDataPlanDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<DataPlanType>(`${process.env.REACT_APP_API_URL}/data-plans/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching data plan!' });
    }
  }
);

export const patchDataPlanDetails = createAsyncThunk<
  DataPlanType,
  { id: number; data: unknown },
  { state: RootReducerState }
>('dataPlans/patchDataPlanDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<DataPlanType>(`${process.env.REACT_APP_API_URL}/data-plans/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating data plan!' });
  }
});

export const deleteDataPlanDetails = createAsyncThunk<
  DataPlanType,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('dataPlans/deleteDataPlanDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<DataPlanType>(`${process.env.REACT_APP_API_URL}/data-plans/${id}`);
    if (fetchList) thunkAPI.dispatch(getDataPlansList(thunkAPI.getState().dataPlansReducer.dataPlansRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting data plan!' });
  }
});

// DATA PLAN TYPES

export const postDataPlanTypeDetails = createAsyncThunk<
  DataPlanTypeType,
  { data: unknown },
  { state: RootReducerState }
>('dataPlanTypes/postDataPlanTypeDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<DataPlanTypeType>(`${process.env.REACT_APP_API_URL}/data-plans/types`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating data plan type!' });
  }
});

export const getDataPlanTypesList = createAsyncThunk<
  PaginationType<DataPlanTypeType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('dataPlanTypes/getDataPlanTypesList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<DataPlanTypeType>>(
      `${process.env.REACT_APP_API_URL}/data-plans/types${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching data plan types!' });
  }
});

export const getDataPlanTypeDetails = createAsyncThunk<DataPlanTypeType, { id: number }, { state: RootReducerState }>(
  'dataPlanTypes/getDataPlanTypeDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<DataPlanTypeType>(`${process.env.REACT_APP_API_URL}/data-plans/types/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching data plan type!' });
    }
  }
);

export const patchDataPlanTypeDetails = createAsyncThunk<
  DataPlanTypeType,
  { id: number; data: unknown },
  {
    state: RootReducerState;
  }
>('dataPlanTypes/patchDataPlanTypeDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<DataPlanTypeType>(
      `${process.env.REACT_APP_API_URL}/data-plans/types/${id}`,
      data
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating data plan type!' });
  }
});

export const deleteDataPlanTypeDetails = createAsyncThunk<
  DataPlanTypeType,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('dataPlanTypes/deleteDataPlanTypeDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<DataPlanTypeType>(`${process.env.REACT_APP_API_URL}/data-plans/types/${id}`);
    if (fetchList)
      thunkAPI.dispatch(getDataPlanTypesList(thunkAPI.getState().dataPlansReducer.dataPlanTypesRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting data plan type!' });
  }
});
